import React from 'react';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core';

/**
 * Shared printable invoice content used by the admin clinic billing view,
 * the selected-patient billing view and the patient payment/subscription view.
 *
 * The header is split 50/50 into:
 *   - Creditor (the payer: patient / insurance / other) on the LEFT
 *   - Debitor  (the clinic / staff issuer) on the RIGHT
 *
 * Fields that do not exist in the backend (bank account / Reg.nr, insurance
 * full address, frozen insurance policy number) are rendered as labelled rows
 * with an em dash placeholder so the layout stays consistent everywhere.
 */

const DASH = '\u2014';

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatDate = (value) => {
  if (!value) return DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? DASH : d.toLocaleDateString();
};

const labelStyle = { fontSize: '11px', color: '#666', marginBottom: '3px' };
const headingStyle = {
  fontSize: '11px',
  fontWeight: 'bold',
  color: '#002D55',
  marginBottom: '8px',
  textTransform: 'uppercase',
};

const Field = ({ label, value }) => (
  <Typography style={labelStyle}>
    <b>{label}:</b> {value || DASH}
  </Typography>
);

const InvoicePrintableContent = ({
  invoice,
  domId = 'invoice-printable-content',
  issuerName,
  issuerSelector = null,
  clinic = {},
  patient = {},
  services = [],
}) => {
  if (!invoice) return null;

  const currency = invoice.currency || 'EUR';
  const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];
  const patientPortion = toNumber(invoice.patient_portion_amount);
  const insurancePortion = toNumber(invoice.insurance_portion_amount);
  const serviceCatalog = Array.isArray(services) ? services : [];
  const serviceByName = new Map(
    serviceCatalog
      .filter((service) => service && service.name)
      .map((service) => [String(service.name).trim().toLowerCase(), service])
  );

  const getServiceCode = (item) => {
    const directCode = String(item.service_code || '').trim();
    if (directCode) {
      return directCode;
    }

    const serviceName = String(item.service_name || '').trim().toLowerCase();
    const matchedService = serviceByName.get(serviceName);
    if (matchedService && matchedService.service_code) {
      return String(matchedService.service_code).trim();
    }

    return '';
  };

  const getLineVatBreakdown = (item) => {
    const serviceName = String(item.service_name || '').trim().toLowerCase();
    const matchedService = serviceByName.get(serviceName);
    const quantity = Math.max(toNumber(item.quantity) || 1, 1);
    const serviceAmount = toNumber(item.line_total) || (toNumber(item.unit_price) * quantity);
    const vatRate = matchedService && matchedService.requires_vat ? toNumber(matchedService.vat_amount_percent) : 0;
    const vatAmount = matchedService && matchedService.requires_vat ? (serviceAmount * vatRate / 100) : 0;
    return {
      serviceAmount,
      vatRate,
      vatAmount,
      totalAmount: serviceAmount + vatAmount,
      requiresVat: Boolean(matchedService && matchedService.requires_vat),
    };
  };

  const renderLineItemRow = (item, idx) => {
    const breakdown = getLineVatBreakdown(item);
    const serviceCode = getServiceCode(item);
    const serviceName = item.service_name || DASH;
    const units = Math.max(toNumber(item.quantity) || 1, 1);

    return (
      <TableRow key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#FAFAFA' : '#FFF', borderBottom: '1px solid #E0E0E0' }}>
        <TableCell style={{ fontSize: '11px', padding: '10px', textAlign: 'left' }}>{formatDate(item.appointment_date)}</TableCell>
        <TableCell style={{ fontSize: '11px', padding: '10px', textAlign: 'left' }}>{serviceCode || DASH}</TableCell>
        <TableCell style={{ fontSize: '11px', padding: '10px', textAlign: 'left' }}>{serviceName}</TableCell>
        <TableCell style={{ fontSize: '11px', padding: '10px', textAlign: 'right', fontWeight: '600' }}>{units}</TableCell>
        <TableCell style={{ fontSize: '11px', padding: '10px', textAlign: 'right', fontWeight: '600' }}>{currency} {breakdown.serviceAmount.toFixed(2)}</TableCell>
        <TableCell style={{ fontSize: '11px', padding: '10px', textAlign: 'right', color: breakdown.requiresVat ? '#C62828' : '#999', fontWeight: '600' }}>{currency} {breakdown.vatAmount.toFixed(2)}</TableCell>
        <TableCell style={{ fontSize: '11px', padding: '10px', textAlign: 'right', color: '#002D55', fontWeight: '600' }}>{currency} {breakdown.totalAmount.toFixed(2)}</TableCell>
      </TableRow>
    );
  };

  const subtotalAmount = lineItems.reduce((sum, item) => sum + getLineVatBreakdown(item).serviceAmount, 0);
  const vatAmount = lineItems.reduce((sum, item) => sum + getLineVatBreakdown(item).vatAmount, 0);
  const totalAmount = subtotalAmount + vatAmount || (toNumber(invoice.total_amount) || patientPortion + insurancePortion);
  const appointmentCount = lineItems.length;

  const recipientType = String(invoice.recipient_type || '').toLowerCase();
  const isInsurancePayer = recipientType === 'insurance';

  // Debitor (clinic / staff issuer)
  const debitorName = issuerName || invoice.care_provider_name || 'Clinic';
  const debitorAddressLine = [
    clinic.address,
    [clinic.postalCode, clinic.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  // Payment terms derived from invoice/due dates when possible.
  let paymentTerms = DASH;
  if (invoice.invoice_date && invoice.due_date) {
    const start = new Date(invoice.invoice_date);
    const end = new Date(invoice.due_date);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
      paymentTerms = `${days} days`;
    }
  }

  // Creditor (payer)
  const patientName = patient.name || invoice.patient_name || DASH;
  const patientAddress = patient.address || '';
  const patientEmail = patient.email || '';

  return (
    <Box id={domId} style={{ padding: '40px', fontFamily: 'Arial, sans-serif', backgroundColor: '#FFF' }}>
      {issuerSelector}

      {/* Invoice title */}
      <Box style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '3px solid #002D55' }}>
        <Typography style={{ fontSize: '28px', fontWeight: 'bold', color: '#002D55' }}>
          INVOICE
        </Typography>
      </Box>

      {/* Creditor / Debitor 50/50 header */}
      <Box style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between' }}>
        {/* Creditor (payer) - LEFT */}
        <Box style={{ width: '48%' }}>
          <Typography style={headingStyle}>Creditor</Typography>
          {isInsurancePayer ? (
            <>
              <Field label="Name" value={invoice.insurer_name} />
              <Field label="Full Address" value="" />
              <Field label="EAN number" value={invoice.insurer_ean} />
              <Field label="Insurance policy number" value={invoice.insurer_policy_number} />
              <Field label="Invoice number" value={invoice.invoice_number} />
            </>
          ) : (
            <>
              <Field label="Name" value={patientName} />
              <Field label="Address" value={patientAddress} />
              <Field label="Postal code" value={patient.postalCode} />
              <Field label="City" value={patient.city} />
              <Field label="Email" value={patientEmail} />
            </>
          )}
        </Box>

        {/* Debitor (clinic / staff issuer) - RIGHT */}
        <Box style={{ width: '48%' }}>
          <Typography style={headingStyle}>Debitor</Typography>
          <Typography style={{ fontSize: '13px', fontWeight: 'bold', color: '#002D55', marginBottom: '6px' }}>
            {debitorName}
          </Typography>
          <Field label="Business address" value={debitorAddressLine} />
          <Field label="CVR" value={clinic.cvr} />
          <Field label="Bank account number / Reg.nr" value={clinic.bankAccount} />
          <Field label="Invoice number" value={invoice.invoice_number} />
          <Field label="Invoice date" value={formatDate(invoice.invoice_date)} />
          <Field label="Payment terms" value={paymentTerms} />
        </Box>
      </Box>

      {/* Summary box */}
      <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
        <Box style={{ width: '48%', backgroundColor: '#F5F5F5', padding: '15px', borderRadius: '4px' }}>
          <Typography style={{ ...headingStyle, marginBottom: '10px' }}>Summary</Typography>
          <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px' }}>
            <span>Accounts:</span>
            <span>{appointmentCount}</span>
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px' }}>
            <span style={{ color: '#002D55', fontWeight: 'bold' }}>Subtotal:</span>
            <span style={{ color: '#002D55', fontWeight: 'bold' }}>{currency} {subtotalAmount.toFixed(2)}</span>
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px' }}>
            <span style={{ color: '#C62828', fontWeight: 'bold' }}>VAT:</span>
            <span style={{ color: '#C62828', fontWeight: 'bold' }}>{currency} {vatAmount.toFixed(2)}</span>
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px' }}>
            <span style={{ color: '#2E7D32', fontWeight: 'bold' }}>Patient Portion:</span>
            <span style={{ color: '#2E7D32', fontWeight: 'bold' }}>{currency} {patientPortion.toFixed(2)}</span>
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
            <span style={{ color: '#1565C0', fontWeight: 'bold' }}>Insurance Portion:</span>
            <span style={{ color: '#1565C0', fontWeight: 'bold' }}>{currency} {insurancePortion.toFixed(2)}</span>
          </Box>
        </Box>
      </Box>

      {/* Line Items Table */}
      <Box style={{ marginBottom: '20px' }}>
        <Table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow style={{ backgroundColor: '#002D55' }}>
              <TableCell style={{ fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', borderBottom: '2px solid #002D55', textAlign: 'left' }}>Date</TableCell>
              <TableCell style={{ fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', borderBottom: '2px solid #002D55', textAlign: 'left' }}>Code</TableCell>
              <TableCell style={{ fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', borderBottom: '2px solid #002D55', textAlign: 'left' }}>Service Name</TableCell>
              <TableCell style={{ fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', borderBottom: '2px solid #002D55', textAlign: 'right' }}>Units</TableCell>
              <TableCell style={{ fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', borderBottom: '2px solid #002D55', textAlign: 'right' }}>Service amount</TableCell>
              <TableCell style={{ fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', borderBottom: '2px solid #002D55', textAlign: 'right' }}>VAT</TableCell>
              <TableCell style={{ fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', borderBottom: '2px solid #002D55', textAlign: 'right' }}>Total amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.length > 0 ? (
              lineItems.map((item, idx) => renderLineItemRow(item, idx))
            ) : (
              <TableRow>
                <TableCell colSpan={7} style={{ fontSize: '11px', padding: '10px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                  No line items available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Totals Section */}
      <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
        <Box style={{ width: '280px' }}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #E0E0E0', fontSize: '12px' }}>
            <span>Subtotal:</span>
            <span>{currency} {totalAmount.toFixed(2)}</span>
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 'bold', color: '#002D55', backgroundColor: '#F5F5F5', padding: '12px' }}>
            <span>TOTAL DUE:</span>
            <span>{currency} {totalAmount.toFixed(2)}</span>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #E0E0E0', fontSize: '10px', color: '#666' }}>
        <Typography style={{ fontSize: '10px', color: '#666', marginBottom: '8px' }}>
          <b>Terms and Conditions:</b> Payment is due within 30 days of invoice date. Please include invoice number with your payment.
        </Typography>
        <Typography style={{ fontSize: '10px', color: '#666' }}>
          Thank you for your business. If you have any questions, please contact us at {clinic.email || 'the clinic email'}{clinic.phone ? ` or ${clinic.phone}` : ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default InvoicePrintableContent;
